import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { AlertCircleIcon } from 'lucide-react'
import {
	Alert, AlertDescription, AlertTitle,
	Button,
	Dialog, DialogContent, DialogFooter, DialogHeader,
	Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@rentflowdev/ui'
import { useUpdateContactStatusMutation } from '@/api'
import { extractErrorMessage } from '@/lib/error-message'
import type { ContactReliability } from '@/types'

const RELIABILITY_OPTIONS: ContactReliability[] = ['POSITIVE', 'MODERATE', 'NEGATIVE']

interface ChangeReliabilityDialogProps {
	open:    boolean
	onOpenChange: (open: boolean) => void
	tenantId:     string
	currentStatus?: ContactReliability
}

export function ChangeReliabilityDialog({ open, onOpenChange, tenantId, currentStatus }: ChangeReliabilityDialogProps) {
	const { t } = useTranslation()
	const [status, setStatus] = useState<ContactReliability>(currentStatus ?? 'POSITIVE')
	const [errorMsg, setErrorMsg] = useState<string | undefined>(undefined)
	const [updateStatus, { isLoading }] = useUpdateContactStatusMutation()

	useEffect(() => {
		if (open) {
			// eslint-disable-next-line react-hooks/set-state-in-effect
			setStatus(currentStatus ?? 'POSITIVE')
			 
			setErrorMsg(undefined)
		}
	}, [open, currentStatus])

	async function handleConfirm() {
		setErrorMsg(undefined)
		try {
			await updateStatus({ id: tenantId, body: { status } }).unwrap()
			onOpenChange(false)
		} catch (err) {
			setErrorMsg(extractErrorMessage(err))
		}
	}

	return (
		<Dialog open={open} onOpenChange={onOpenChange} disablePointerDismissal>
			<DialogContent
				showCloseButton={false}
				className="flex flex-col p-0 gap-0 overflow-visible rounded-(--radius-lg)"
				style={{ width: 420, maxWidth: '95vw' }}
			>
				<DialogHeader title={t('tenants.reliabilityDialog.title')} />

				<div className="flex flex-col gap-3 px-6 py-4">
					{errorMsg !== undefined && (
						<Alert variant="destructive">
							<AlertCircleIcon className="size-4" />
							<AlertTitle>{t('tenants.reliabilityDialog.error')}</AlertTitle>
							{errorMsg && <AlertDescription>{errorMsg}</AlertDescription>}
						</Alert>
					)}
					<label className="flex flex-col gap-2 text-sm text-(--color-foreground)">
						{t('tenants.reliabilityDialog.statusLabel')}
						<Select value={status} onValueChange={(v) => setStatus(v as ContactReliability)}>
							<SelectTrigger size="md" className="w-full">
								<SelectValue />
							</SelectTrigger>
							<SelectContent>
								{RELIABILITY_OPTIONS.map((opt) => (
									<SelectItem key={opt} value={opt}>{t(`tenants.reliability.${opt.toLowerCase()}`)}</SelectItem>
								))}
							</SelectContent>
						</Select>
					</label>
				</div>

				<DialogFooter>
					<Button type="button" variant="secondary" onClick={() => onOpenChange(false)}>
						{t('common.cancel')}
					</Button>
					<Button type="button" onClick={handleConfirm} disabled={isLoading}>
						{t('common.save')}
					</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	)
}
